// The search form's admin-configured buttons, and what pressing one does.
//
// Two things are deliberate in here:
//
//   1. Everything downstream reads an ARRAY, even though the stored config is
//      still one button (actionButtonEnabled/Label/Url/Target/OpenAs). The day
//      that config grows a real `actionButtons` list, it is a backend key and an
//      admin editor — the renderer, the behaviours and their tests already
//      handle N buttons, and no stored document needs migrating.
//
//   2. HOW a button opens is config, not code. "Same page" was the only option
//      that ever existed, "New tab" was an anchor-target flag, and a popup was
//      impossible; all three are now one value an admin picks, and a fourth
//      (calling an API, as row actions already do) is one more entry in
//      ACTION_BEHAVIORS rather than another branch in the component.

import { getSearchPopup, popupPathOf } from './searchPopupRegistry';

export const OPEN_AS_OPTIONS = [
  { value: 'self', label: 'Same page' },
  { value: 'newTab', label: 'New tab' },
  { value: 'popup', label: 'Popup' },
];

const OPEN_AS_VALUES = OPEN_AS_OPTIONS.map((option) => option.value);

// Forms saved before openAs existed stored only an anchor target, so it is what
// the mode is derived from. Anything unrecognised lands on 'self' — the
// behaviour every one of those forms already had.
export function resolveOpenAs(button) {
  const openAs = String(button?.openAs ?? '').trim();
  if (OPEN_AS_VALUES.includes(openAs)) return openAs;
  return button?.target === '_blank' ? 'newTab' : 'self';
}

function normalizeButton(raw, index) {
  return {
    key: raw?.key || `actionButton${index}`,
    label: raw?.label || 'Open',
    url: raw?.url ?? '',
    target: raw?.target ?? '_self',
    openAs: raw?.openAs ?? '',
  };
}

// One reader for both config shapes. `actionButtons` wins where it exists so a
// form that has moved on is never also given its legacy single button.
export function readActionButtons(schema) {
  if (Array.isArray(schema?.actionButtons)) {
    return schema.actionButtons
      .filter((button) => button && button.enabled !== false)
      .map(normalizeButton);
  }
  if (!schema?.actionButtonEnabled) return [];
  return [normalizeButton({
    key: 'actionButton',
    label: schema.actionButtonLabel,
    url: schema.actionButtonUrl,
    target: schema.actionButtonTarget,
    openAs: schema.actionButtonOpenAs,
  }, 0)];
}

// `destination` is always the RESOLVED path — safeNavTarget has already
// expanded the :tokens and rejected external/scheme URLs — so no behaviour here
// ever sees an admin-supplied string raw. Deliberately NOT called `target`:
// that name belongs to the button's legacy anchor value ("_blank"), which is
// what resolveOpenAs reads, and conflating the two made every legacy new-tab
// button navigate in place instead.
export const ACTION_BEHAVIORS = {
  self: ({ destination, navigate }) => navigate(destination),
  newTab: ({ destination }) => window.open(destination, '_blank', 'noopener'),
  // Falls through to navigation when the app has not registered a component for
  // this route: a half-finished config gets the old behaviour, never a modal
  // with nothing in it. It says so out loud, because a config pointing at an
  // unregistered route looks exactly like "the popup feature is broken" from
  // the screen, and this line is the difference between a five-minute answer
  // and an afternoon.
  popup: ({ destination, openPopup, navigate }) => {
    if (getSearchPopup(destination) && openPopup(destination)) return;
    console.warn(
      `[SearchBuilderV1] Button is configured to open a popup, but no page is registered for `
      + `"${popupPathOf(destination)}" — navigating instead. Register it with `
      + 'registerSearchPopup(path, Component), or point the button at a route that is.',
    );
    navigate(destination);
  },
};

// What the button will ACTUALLY do, which is not always what resolveOpenAs
// reads off the document.
//
// A destination pointing at a route the app registered as a popup body, on a
// button whose mode was never chosen, opens the popup. The mode being
// overridden there is not a decision anyone made: it was derived from
// actionButtonTarget, a key that predates the setting entirely, so an admin who
// pointed the URL at a popup route by hand would otherwise get a full page and
// no clue why.
//
// Everything explicit still wins — including an explicit "self", so pointing at
// the route and asking for the whole page is still expressible, and "_blank",
// which is a real expressed intent rather than a default.
export function effectiveOpenAs(button, destination) {
  const configured = resolveOpenAs(button);
  const chosen = Boolean(String(button?.openAs ?? '').trim());
  if (!chosen && configured === 'self' && getSearchPopup(destination)) return 'popup';
  return configured;
}

export function runActionButton(button, context) {
  if (!context?.destination) return;
  const behavior = ACTION_BEHAVIORS[effectiveOpenAs(button, context.destination)];
  (behavior ?? ACTION_BEHAVIORS.self)(context);
}
